import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, CardBody, Col, Row } from "reactstrap";

import BookingCatalogSkeleton from "./BookingCatalogSkeleton";
import { PublicBookingService } from "../../../services/publicBookingService";

interface CatalogStepProps {
  services: PublicBookingService[];
  loading: boolean;
  selectedService: PublicBookingService | null;
  onSelectService: (service: PublicBookingService) => void;
}

const CatalogStep: React.FC<CatalogStepProps> = ({
  services,
  loading,
  selectedService,
  onSelectService,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <>
        <p className="text-muted mb-3">{t("public_booking.services.loading")}</p>
        <BookingCatalogSkeleton />
      </>
    );
  }

  if (services.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="avatar-md mx-auto mb-3">
          <span className="avatar-title rounded-circle bg-light text-muted fs-24">
            <i className="ri-scissors-cut-line" />
          </span>
        </div>
        <h5 className="mb-2">{t("public_booking.services.emptyTitle")}</h5>
        <p className="text-muted mb-0">{t("public_booking.services.emptyDescription")}</p>
      </div>
    );
  }

  return (
    <Row className="g-3">
      {services.map((service) => {
        const isSelected = selectedService?.id === service.id;

        return (
          <Col xs={12} key={service.id}>
            <Card
              className={`border shadow-none mb-0 h-100${isSelected ? " border-primary border-2" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => onSelectService(service)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectService(service);
                }
              }}
            >
              <CardBody className="d-flex gap-3 align-items-start">
                <div
                  className="rounded flex-shrink-0 bg-light d-flex align-items-center justify-content-center"
                  style={{ width: 72, height: 72 }}
                >
                  {service.cover_url ? (
                    <img
                      src={service.cover_url}
                      alt=""
                      className="rounded object-fit-cover w-100 h-100"
                    />
                  ) : (
                    <i className="ri-scissors-cut-line fs-24 text-muted" />
                  )}
                </div>

                <div className="flex-grow-1 min-w-0">
                  {service.category?.name ? (
                    <span className="badge bg-light text-muted mb-2">{service.category.name}</span>
                  ) : null}
                  <h5 className="fs-16 mb-1">{service.name}</h5>
                  {service.description ? (
                    <p className="text-muted small mb-2">{service.description}</p>
                  ) : null}
                  <p className="text-muted mb-2">
                    {t("public_booking.services.durationMinutes", {
                      minutes: service.duration_minutes,
                    })}
                  </p>
                  <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                    <div>
                      <span className="fw-semibold text-primary">{service.price_display}</span>
                      {service.promo_active && service.list_price_display ? (
                        <small className="text-muted text-decoration-line-through d-block">
                          {service.list_price_display}
                        </small>
                      ) : null}
                      {service.price_is_from ? (
                        <small className="text-muted d-block">
                          {t("public_booking.services.priceFromHint")}
                        </small>
                      ) : null}
                    </div>
                    <Button
                      color={isSelected ? "primary" : "soft-primary"}
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelectService(service);
                      }}
                    >
                      {isSelected
                        ? t("public_booking.common.selected")
                        : t("public_booking.common.select")}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};

export default CatalogStep;
